import React from "react";
import {
  TagIcon,
  BriefcaseIcon,
  CalendarIcon,
  ArrowRightIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

const fallbackImg = "/project.png";

/* ---------- Helpers ---------- */
const formatDate = (d) => {
  if (!d) return null;

  // Firestore Timestamp { seconds, nanoseconds }
  if (d.seconds) return new Date(d.seconds * 1000).toLocaleDateString();

  // ISO string or number
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt.toLocaleDateString();
};

/* ---------- Small UI Bits ---------- */
const Chip = ({ children, icon: Icon }) => (
  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700 shadow-sm">
    {Icon && <Icon className="h-3.5 w-3.5 mr-1 text-gray-500" />}
    {children}
  </span>
);

const ScorePill = ({ score }) => {
  if (typeof score !== "number") return null;
  return (
    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-sky-100 via-fuchsia-100 to-emerald-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 ring-1 ring-black/5">
      Match {Math.round(score * 100)}%
    </span>
  );
};

/* ---------- ProjectCard ---------- */
const ProjectCard = ({
  project = {},
  meta = {}, // { why: string[], score: number }
  className = "",
  onView,
  onPress,
}) => {
  const {
    title,
    oneLiner,
    description,
    image,
    category,
    tags = [],
    openRoles = [],
    deadline,
    createdAt,
    owner,
  } = project;

  const why = Array.isArray(meta.why) ? meta.why : [];
  const score = typeof meta.score === "number" ? meta.score : null;

  return (
    <div
      className={`group relative rounded-2xl p-[1px] bg-gradient-to-tr from-sky-200/60 via-fuchsia-200/60 to-emerald-200/60 transition-all hover:shadow-lg hover:-translate-y-0.5 cursor-pointer ${className}`}
      onClick={() => onPress?.(project)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onPress?.(project);
      }}
    >
      <div className="h-full rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden flex flex-col">
        {/* Header Image */}
        <div className="h-40 w-full bg-neutral-100 overflow-hidden">
          <img
            src={image || fallbackImg}
            alt={title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            onError={(e) => (e.currentTarget.src = fallbackImg)}
          />
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1 gap-3">
          {/* Title + score */}
          <div className="flex items-center gap-2">
            <div className="font-semibold text-gray-900 truncate text-base">
              {title || "Untitled Project"}
            </div>
            <ScorePill score={score} />
          </div>

          {oneLiner && (
            <div className="text-sm text-gray-600 line-clamp-2 leading-snug">
              {oneLiner}
            </div>
          )}

          {/* Chips: category + tags */}
          <div className="flex flex-wrap gap-1.5 mt-1">
            {category && <Chip icon={TagIcon}>{category}</Chip>}
            {tags.slice(0, 3).map((t, i) => (
              <Chip key={i}>{t}</Chip>
            ))}
            {tags.length > 3 && <Chip>+{tags.length - 3}</Chip>}
          </div>

          {/* Open roles */}
          {openRoles.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {openRoles.slice(0, 2).map((r, i) => (
                <Chip key={i} icon={BriefcaseIcon}>
                  {r.roleTitle}
                </Chip>
              ))}
              {openRoles.length > 2 && (
                <Chip>+{openRoles.length - 2} more</Chip>
              )}
            </div>
          )}

          {/* Why chips (AI reasons) */}
          {why.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {why.slice(0, 3).map((w, i) => (
                <Chip key={i}>{w}</Chip>
              ))}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Footer meta */}
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500 border-t pt-2">
            {createdAt && (
              <span className="flex items-center">
                <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                {formatDate(createdAt)}
              </span>
            )}
            {deadline && (
              <span className="ml-auto flex items-center text-rose-600">
                ⏳ {formatDate(deadline)}
              </span>
            )}
          </div>

          {/* Actions */}
          {onView && (
            <div className="mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView(project);
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 transition"
              >
                View project
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
